import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FeedHeader from './components/FeedHeader';
import FeedStickyHeader from './components/FeedStickyHeader';
import FeedFilterBar from './components/FeedFilterBar';
import FeedGroupedList from './components/FeedGroupedList';
import InsightsSidebar from './components/InsightsSidebar';
import EmptyState from './components/EmptyState';
import { InsightsListSkeleton } from './components/InsightsSkeleton';
import useInsightFeed from './hooks/useInsightFeed';
import useFiData from './hooks/useFiData';
import { useWatchlistSidebarData, usePortfolioSidebarData } from './hooks/useSidebarData';
import { instrumentApi } from '../../../utils/api';

function buildHighlights(items, marketData) {
    const countMap = {};
    for (const it of items) {
        if (it.canonical_id) countMap[it.canonical_id] = (countMap[it.canonical_id] || 0) + 1;
    }
    const seen = new Set();
    const out = [];
    for (const it of items) {
        if (!it.canonical_id || seen.has(it.canonical_id)) continue;
        seen.add(it.canonical_id);
        const m = marketData?.[String(it.symbol || '').toUpperCase()];
        out.push({
            canonical_id: it.canonical_id,
            symbol: it.symbol,
            name: m?.companyName || m?.name || it.attributes?.company_name || it.venue || '',
            price: m?.priceClose ?? it.structured?.price ?? null,
            change_pct: m?.pctChange ?? it.structured?.change_pct ?? null,
            report_count: countMap[it.canonical_id] || 0,
        });
    }
    return out;
}

export default function InsightsPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [categories, setCategories] = React.useState([]);
    const [showStickyHeader, setShowStickyHeader] = React.useState(false);

    // Derive all filter state from URL params
    const query    = searchParams.get('q') || '';
    const symbols  = (searchParams.get('symbol') || '').split(',').map((s) => s.trim()).filter(Boolean);
    const category = searchParams.get('category') || '';
    const period   = searchParams.get('period') || 'all';
    const scope    = searchParams.get('scope') || 'all';
    const unreadOnly = searchParams.get('unread') === '1';
    const savedOnly  = searchParams.get('saved') === '1';

    const setParam = (key, value, defaultValue) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (!value || value === defaultValue) next.delete(key);
            else next.set(key, value);
            return next;
        }, { replace: true });
    };

    const setQuery    = (v) => setParam('q', v, '');
    const setCategory = (v) => setParam('category', v, '');
    const setPeriod   = (v) => setParam('period', v, 'all');
    const setScope    = (v) => setParam('scope', v, 'all');
    const setUnreadOnly = (v) => setParam('unread', v ? '1' : '', '');
    const setSavedOnly  = (v) => setParam('saved',  v ? '1' : '', '');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await instrumentApi.listInsightCategoriesPublic();
                if (cancelled) return;
                setCategories(Array.isArray(res) ? res : []);
            } catch {
                /* category dropdown is optional — silent on failure */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const { items, loading, loadingMore, error, hasMore, loadMore } = useInsightFeed({
        query,
        symbol: symbols.length === 1 ? symbols[0] : symbols.length > 1 ? symbols.join(',') : undefined,
        category: category || undefined,
        period,
        sort: query && query.trim() ? 'relevance' : 'newest',
        pageSize: 20,
    });

    const tickers = useMemo(
        () => [...new Set(items.map((i) => i.symbol).filter(Boolean))],
        [items],
    );
    const { data: marketData } = useFiData(tickers);

    const highlights = useMemo(() => buildHighlights(items, marketData), [items, marketData]);
    const watchlistData = useWatchlistSidebarData();
    const portfolioData = usePortfolioSidebarData();

    const watchlistSymbols = useMemo(() => {
        const set = new Set();
        watchlistData?.items?.forEach((h) => h.symbol && set.add(h.symbol.toUpperCase()));
        return set;
    }, [watchlistData]);

    const portfolioSymbols = useMemo(() => {
        const set = new Set();
        portfolioData?.items?.forEach((h) => h.symbol && set.add(h.symbol.toUpperCase()));
        return set;
    }, [portfolioData]);
    const latestSnapshot = items[0]?.snapshot_at;
    const trackedCount = new Set(items.map((i) => i.canonical_id).filter(Boolean)).size;

    const openDetail = (item) => {
        if (!item?.id) return;
        navigate(`/insights/${encodeURIComponent(item.id)}`);
    };

    const setSymbols = (updater) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            const current = (prev.get('symbol') || '').split(',').map((s) => s.trim()).filter(Boolean);
            const updated = typeof updater === 'function' ? updater(current) : updater;
            if (updated.length) next.set('symbol', updated.join(','));
            else next.delete('symbol');
            return next;
        }, { replace: true });
    };

    const handleSelectSymbol = (s) => {
        if (!s.symbol) return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('q');
            const current = (prev.get('symbol') || '').split(',').map((x) => x.trim()).filter(Boolean);
            const updated = current.includes(s.symbol)
                ? current.filter((x) => x !== s.symbol)
                : [...current, s.symbol];
            if (updated.length) next.set('symbol', updated.join(','));
            else next.delete('symbol');
            return next;
        }, { replace: true });
    };

    const handleRemoveSymbol = (sym) => setSymbols((prev) => prev.filter((s) => s !== sym));
    const handleClearSymbols = () => setSymbols([]);

    const scrollRef = React.useRef(null);

    const handleScroll = (e) => {
        const scrolled = e.target.scrollTop > 100;
        if (scrolled !== showStickyHeader) {
            setShowStickyHeader(scrolled);
        }
    };

    return (
        <div className="flex flex-col h-full bg-[#F8FDFC] text-[#111827] pt-[var(--header-height,0px)] md:pt-0">
            <div className="flex-1 flex overflow-hidden min-h-0">
                <div className="flex-1 flex flex-col min-w-0 relative">
                    <FeedStickyHeader
                        scrollRef={scrollRef}
                        query={query}
                        onSubmit={setQuery}
                        onSelectSymbol={handleSelectSymbol}
                        onSelectInsight={openDetail}
                        isVisible={showStickyHeader}
                        symbols={symbols}
                        onRemoveSymbol={handleRemoveSymbol}
                        onClearSymbols={handleClearSymbols}
                        // Filter props
                        period={period} onPeriod={setPeriod}
                        categories={categories} category={category} onCategory={setCategory}
                        scope={scope} onScope={setScope}
                        unreadOnly={unreadOnly} onToggleUnread={() => setUnreadOnly(!unreadOnly)}
                        savedOnly={savedOnly} onToggleSaved={() => setSavedOnly(!savedOnly)}
                    />

                    <main
                        ref={scrollRef}
                        onScroll={handleScroll}
                        className="flex-1 overflow-y-auto overflow-x-hidden min-w-0"
                    >
                        <div className="bg-white border-b border-[#ECECED]">
                            <div className="px-[16px] md:px-[32px]">
                                <div className="max-w-[1472px] mx-auto">
                                    <FeedHeader
                                        trackedCount={trackedCount}
                                        lastUpdated={latestSnapshot}
                                        compact={false}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="px-[16px] md:px-[32px] pb-[32px] md:pb-[40px]">
                            <div className="max-w-[733px] mx-auto flex flex-col">
                                <div className='my-[16px]'>
                                    <FeedFilterBar
                                        query={query} onSubmit={setQuery}
                                        onSelectSymbol={handleSelectSymbol}
                                        onSelectInsight={openDetail}
                                        period={period} onPeriod={setPeriod}
                                        categories={categories} category={category} onCategory={setCategory}
                                        scope={scope} onScope={setScope}
                                        unreadOnly={unreadOnly} onToggleUnread={() => setUnreadOnly(!unreadOnly)}
                                        savedOnly={savedOnly} onToggleSaved={() => setSavedOnly(!savedOnly)}

                                    />
                                </div>
                                {symbols.length > 0 && (
                                    <div className="flex items-center flex-wrap gap-[8px] mb-[16px]">
                                        {symbols.map((sym) => (
                                            <span key={sym} className="inline-flex items-center gap-[6px] rounded-full bg-[#EEF2FF] text-[#4F46E5] px-[10px] py-[4px] text-[12px] font-medium">
                                                {sym}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveSymbol(sym)}
                                                    className="ml-[4px] hover:text-[#312E81] flex items-center justify-center w-[16px] h-[16px] rounded-full hover:bg-[#E5E7EB] transition-colors"
                                                    aria-label={`Xoá lọc mã ${sym}`}
                                                >
                                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </button>
                                            </span>
                                        ))}
                                        {symbols.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={handleClearSymbols}
                                                className="text-[12px] text-[#6B7280] hover:text-[#111827] transition-colors"
                                            >
                                                Xoá tất cả
                                            </button>
                                        )}
                                    </div>
                                )}
                                {loading && <InsightsListSkeleton groups={2} />}
                                {error && <div className="py-[20px] text-[#DC2626] text-[13px]">{error}</div>}
                                {!loading && !error && items.length === 0 && (
                                    <EmptyState
                                        title="Chưa có báo cáo phù hợp"
                                        description="Thử bỏ bộ lọc hoặc đổi khoảng thời gian để xem các báo cáo mới nhất từ Nebula."
                                    />
                                )}
                                {!loading && !error && items.length > 0 && (
                                    <FeedGroupedList items={items} marketData={marketData} onOpen={openDetail} watchlistSymbols={watchlistSymbols} portfolioSymbols={portfolioSymbols} />
                                )}
                                {!loading && hasMore && (
                                    <div className="flex justify-center pt-[24px]">
                                        <button
                                            type="button"
                                            onClick={loadMore}
                                            disabled={loadingMore}
                                            className="inline-flex items-center justify-center gap-[8px] h-[36px] px-[28px] min-w-[160px] text-[13px] font-medium text-[#0F172A] bg-white border border-[#E5E7EB] rounded-[8px] shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-[#F9FAFB] hover:border-[#D1D5DB] active:bg-[#F3F4F6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {loadingMore && (
                                                <svg className="animate-spin h-[14px] w-[14px] text-[#6B7280]" viewBox="0 0 24 24" fill="none">
                                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                                                    <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                                </svg>
                                            )}
                                            {loadingMore ? 'Đang tải' : 'Xem thêm'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </main>

                </div>

                <aside className="hidden lg:flex flex-col w-[453px] shrink-0 overflow-hidden bg-white border-l border-[#EDEFF3]">
                    <InsightsSidebar
                        highlights={highlights}
                        watchlistData={watchlistData}
                        portfolioData={portfolioData}
                        selectedSymbols={symbols}
                        onPick={(h) => handleSelectSymbol({ symbol: h.symbol })}
                    />
                </aside>
            </div>
        </div>
    );
}
